/** Organisation role API values */
import { getProjectTerms } from '@/utils/projectTerminology'

export const ORG_ROLE = {
  ADMIN: 'admin',
  EDITOR: 'editor',
  MEMBER: 'member',
}

/** Project membership role API values */
export const PROJECT_ROLE = {
  MANAGER: 'manager',
  MEMBER: 'member',
}

/** Effective role from ProjectSerializer.current_user_role */
export const EFFECTIVE_ROLE = {
  OWNER: 'owner',
  ORG_ADMIN: 'org_admin',
  ORG_EDITOR: 'org_editor',
  PROJECT_MANAGER: 'project_manager',
  PROJECT_MEMBER: 'project_member',
  SITE_MEMBER: 'site_member',
}

export const ORG_ROLE_LABELS = {
  [ORG_ROLE.ADMIN]: 'Admin',
  [ORG_ROLE.EDITOR]: 'Manager',
  [ORG_ROLE.MEMBER]: 'Member',
}

export const PROJECT_ROLE_LABELS = {
  [PROJECT_ROLE.MEMBER]: 'Participant',
}

export function getOrgRole(org) {
  if (!org || org.id === 0) return ORG_ROLE.ADMIN
  return org.role || null
}

export function getProjectRole(project) {
  return project?.current_user_role || null
}

export function getOrgRoleLabel(role) {
  return ORG_ROLE_LABELS[role] || role
}

export function getProjectRoleLabel(role, org) {
  if (role === PROJECT_ROLE.MANAGER) {
    return getProjectTerms(org).managerRoleLabel
  }
  return PROJECT_ROLE_LABELS[role] || role
}

export function isPersonalWorkspace(org) {
  return !org || org.id === 0
}

export function isOrgAdmin(org) {
  return getOrgRole(org) === ORG_ROLE.ADMIN
}

export function isOrgEditor(org) {
  return getOrgRole(org) === ORG_ROLE.EDITOR
}

export function isOrgMember(org) {
  return getOrgRole(org) === ORG_ROLE.MEMBER
}

export function isOrgAdminOrEditor(org) {
  const role = getOrgRole(org)
  return role === ORG_ROLE.ADMIN || role === ORG_ROLE.EDITOR
}

function hasEffectiveRole(role, ...allowed) {
  // `owner` is the highest-privilege effective role (project creator / user_id
  // match). It must pass every project/site-scoped capability check, even
  // inside an org where the backend may return `owner` instead of `org_admin`.
  if (role === EFFECTIVE_ROLE.OWNER) return true
  return allowed.includes(role)
}

export function getSiteEffectiveRole(site, project, org) {
  if (!site) return null
  if (isPersonalWorkspace(org)) {
    return EFFECTIVE_ROLE.OWNER
  }
  const projectRole = getProjectRole(project)
  if (projectRole) return projectRole
  return EFFECTIVE_ROLE.SITE_MEMBER
}

export function canManageOrgSettings(org) {
  return isOrgAdmin(org)
}

export function canCreateProject(org) {
  if (isPersonalWorkspace(org)) return true
  return isOrgAdminOrEditor(org)
}

/**
 * Footer "Upgrade Plan" when the Create menu is hidden. Org members without
 * create permission should not see upgrade — they cannot manage billing.
 */
export function canShowNavigationUpgradePlan(
  org,
  {
    canShowCreateMenu,
    canManage,
    hasValidSubscription,
    projectsAvailable,
    siteLimitReached,
  }
) {
  if (canShowCreateMenu) return false

  if (isPersonalWorkspace(org)) {
    return (
      !hasValidSubscription || !projectsAvailable || siteLimitReached
    )
  }

  if (!isOrgAdmin(org)) return false

  return !canManage || !projectsAvailable || siteLimitReached
}

export function canDeleteProject(org, projectRole) {
  if (isPersonalWorkspace(org)) return true
  return hasEffectiveRole(
    projectRole,
    EFFECTIVE_ROLE.ORG_ADMIN,
    EFFECTIVE_ROLE.ORG_EDITOR
  )
}

export function canEditProject(org, projectRole, projectUserId, currentUserId) {
  if (isPersonalWorkspace(org)) {
    return projectUserId === currentUserId
  }
  return hasEffectiveRole(
    projectRole,
    EFFECTIVE_ROLE.ORG_ADMIN,
    EFFECTIVE_ROLE.ORG_EDITOR,
    EFFECTIVE_ROLE.PROJECT_MANAGER
  )
}

export function canCreateSiteInProject(org, projectRole) {
  if (isPersonalWorkspace(org)) return true
  return hasEffectiveRole(
    projectRole,
    EFFECTIVE_ROLE.ORG_ADMIN,
    EFFECTIVE_ROLE.ORG_EDITOR,
    EFFECTIVE_ROLE.PROJECT_MANAGER
  )
}

export function canDeploySite(org, projectRole) {
  if (isPersonalWorkspace(org)) return true
  return hasEffectiveRole(
    projectRole,
    EFFECTIVE_ROLE.ORG_ADMIN,
    EFFECTIVE_ROLE.ORG_EDITOR,
    EFFECTIVE_ROLE.PROJECT_MANAGER,
    EFFECTIVE_ROLE.PROJECT_MEMBER,
    EFFECTIVE_ROLE.SITE_MEMBER
  )
}

export function canInviteOrgMember(org) {
  return isOrgAdmin(org)
}

export function canInviteProjectMember(org, projectRole) {
  if (isPersonalWorkspace(org)) return true
  return hasEffectiveRole(
    projectRole,
    EFFECTIVE_ROLE.ORG_ADMIN,
    EFFECTIVE_ROLE.ORG_EDITOR,
    EFFECTIVE_ROLE.PROJECT_MANAGER
  )
}

export function canInviteSiteMember(org, projectRole) {
  if (isPersonalWorkspace(org)) return true
  return hasEffectiveRole(
    projectRole,
    EFFECTIVE_ROLE.ORG_ADMIN,
    EFFECTIVE_ROLE.ORG_EDITOR,
    EFFECTIVE_ROLE.PROJECT_MANAGER
  )
}

export function canManageProjectMembers(org, projectRole, projectUserId, currentUserId) {
  if (isPersonalWorkspace(org)) {
    return projectUserId === currentUserId
  }
  return hasEffectiveRole(
    projectRole,
    EFFECTIVE_ROLE.ORG_ADMIN,
    EFFECTIVE_ROLE.ORG_EDITOR,
    EFFECTIVE_ROLE.PROJECT_MANAGER
  )
}

export function canViewProjectMembers(org, projectRole, projectUserId, currentUserId) {
  return canManageProjectMembers(org, projectRole, projectUserId, currentUserId)
}

export function canManageSiteMembers(org, projectRole, siteUserId, currentUserId) {
  if (isPersonalWorkspace(org)) {
    return siteUserId === currentUserId
  }
  return hasEffectiveRole(
    projectRole,
    EFFECTIVE_ROLE.ORG_ADMIN,
    EFFECTIVE_ROLE.ORG_EDITOR,
    EFFECTIVE_ROLE.PROJECT_MANAGER
  )
}

export function canChangeOrgRoles(org) {
  return isOrgAdmin(org)
}

export function canAssignProjectManager(org, projectRole) {
  if (isPersonalWorkspace(org)) return true
  return hasEffectiveRole(
    projectRole,
    EFFECTIVE_ROLE.ORG_ADMIN,
    EFFECTIVE_ROLE.ORG_EDITOR
  )
}

export function getProjectInviteRoles(org, projectRole) {
  const terms = getProjectTerms(org)
  if (canAssignProjectManager(org, projectRole)) {
    return [
      { label: terms.managerRoleLabel, value: PROJECT_ROLE.MANAGER },
      { label: PROJECT_ROLE_LABELS.member, value: PROJECT_ROLE.MEMBER },
    ]
  }
  return [{ label: PROJECT_ROLE_LABELS.member, value: PROJECT_ROLE.MEMBER }]
}

export function getOrgInviteRoles() {
  return [
    { label: ORG_ROLE_LABELS.admin, value: ORG_ROLE.ADMIN },
    { label: ORG_ROLE_LABELS.editor, value: ORG_ROLE.EDITOR },
    { label: ORG_ROLE_LABELS.member, value: ORG_ROLE.MEMBER },
  ]
}

export function hasImplicitOrgProjectAccess(orgMember, org) {
  if (!orgMember || !org || org.id === 0) return false
  if (orgMember.user_id === org.user_id) return true
  return (
    orgMember.role === ORG_ROLE.ADMIN || orgMember.role === ORG_ROLE.EDITOR
  )
}

export function isInvitableOrgProjectMember(orgMember, org) {
  if (!orgMember?.email || !org || org.id === 0) return false
  return !hasImplicitOrgProjectAccess(orgMember, org)
}

export function isOrgOwner(org, currentUserId) {
  return Boolean(org && org.id !== 0 && org.user_id === currentUserId)
}

export function canManageOrgMemberProjectAccess(org, currentUserId) {
  if (isPersonalWorkspace(org)) return false
  return isOrgOwner(org, currentUserId) || isOrgAdmin(org)
}

export function getOrgMemberProjectRoleOptions(org) {
  const terms = getProjectTerms(org)
  return [
    { label: terms.managerRoleLabel, value: PROJECT_ROLE.MANAGER },
    { label: PROJECT_ROLE_LABELS.member, value: PROJECT_ROLE.MEMBER },
  ]
}

export function getOrgMemberOrgRole(item) {
  if (!item) return null
  if (typeof item.role === 'object') return item.role.value
  return item.role
}

export function canExpandOrgMemberProjectAccess(item, org, currentUserId) {
  if (!item || item.invited) return false
  if (hasImplicitOrgProjectAccess(item, org)) return false
  if (getOrgMemberOrgRole(item) !== ORG_ROLE.MEMBER) return false
  return canManageOrgMemberProjectAccess(org, currentUserId)
}

export function getProjectMemberRole(item) {
  if (!item) return null
  if (typeof item.role === 'object') return item.role.value
  return item.role
}

export function shouldManageSiteAccessForProjectMembership(membership) {
  if (!membership?.project_user_id) return false
  return membership.role === PROJECT_ROLE.MEMBER
}

export function canExpandProjectMemberSiteAccess(
  item,
  org,
  projectRole,
  projectUserId,
  currentUserId
) {
  if (!item || item.invited || item.implicit_access) return false
  if (getProjectMemberRole(item) !== PROJECT_ROLE.MEMBER) return false
  return canManageSiteMembers(org, projectRole, projectUserId, currentUserId)
}
